import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Container, Grid, IconButton, InputBase, Stack } from '@mui/material';
import { AuditionCard } from './AuditionCard';
import { IAudition, IRole } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';
import { AuditionWrapper } from './AuditionWrapper';
import { IAuditionFilter } from '../../redux/audition/types';
import { isString } from 'lodash';
import { useDispatch } from 'react-redux';
import { setAudition } from '../../redux/audition/actions';
import { isEmpty } from '../../utils/lodash';
import { EmptySearchBlock } from '../EsSidebar/EsSearch/EmptySearchBlock';
import { FONT_ENUM } from '../../types/global/fonts.types';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';

const niceColors = require('nice-color-palettes');

export interface IAuditionGrid {
    auditions: IAudition[];
    selectedFilters?: IAuditionFilter;
    deleteFilter?: (filter?: IAuditionFilter) => void;
    onUpdateFilter?: (filter: any) => void;
    onApplyClick: (role: IRole) => void;
    toggleFilterInMobile?: VoidFunction;
    totalResults?: number;
}

const EXCLUDED_KEYS = ['min_age', 'job_start_date'];

export const AuditionGrid: React.FC<IAuditionGrid> = ({
    auditions,
    selectedFilters,
    deleteFilter,
    onUpdateFilter,
    totalResults = 0,
    toggleFilterInMobile,
}) => {
    const [filter, setFilter] = useState(['total']);
    const [selectedAudition, setSelectedAudition] = useState<IAudition | null>(null);
    const [open, setOpen] = useState(false);
    const colorsNice = [...niceColors[67], ...niceColors[1], ...niceColors[29], ...niceColors[71], ...niceColors[81]];
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();

    const handleOnViewAndApplyClick = (audition: IAudition) => {
        setSelectedAudition(audition);
        setOpen(!open);
        dispatch(setAudition(audition));
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedAudition(null);
        dispatch(setAudition(undefined));
    };

    const handleDelete = (label: string) => {
        if (!selectedFilters) return;
        if (label && label.toLowerCase() === 'total') return selectedFilters;

        const updatedFilters = Object.keys(selectedFilters || {}).reduce((acc, key) => {
            const filter = selectedFilters[key as keyof IAuditionFilter];
            const { displayValue, value } = filter;

            if (Array.isArray(displayValue) && Array.isArray(value)) {
                const filteredDisplayValue = displayValue.filter(item => item.toLowerCase() !== label.toLowerCase());
                const filteredValue = value.filter(item => item.toLowerCase() !== label.toLowerCase());
                if (filteredDisplayValue.length > 0) {
                    acc[key as keyof IAuditionFilter] = {
                        ...filter,
                        displayValue: filteredDisplayValue,
                        value: filteredValue,
                    };
                }
            } else if (
                isString(displayValue) &&
                isString(value) &&
                displayValue.toLowerCase() !== label.toLowerCase() &&
                value.toLowerCase() !== label.toLowerCase() &&
                !EXCLUDED_KEYS.includes(key)
            ) {
                acc[key as keyof IAuditionFilter] = { ...filter, displayValue, value };
            }

            return acc;
        }, {} as typeof selectedFilters);

        onUpdateFilter && onUpdateFilter(updatedFilters);
        deleteFilter && deleteFilter(updatedFilters);
        return updatedFilters;
    };

    const extractFilters = useCallback(() => {
        const result: any[] = [];
        selectedFilters &&
            Object.keys(selectedFilters).forEach(key => {
                if (!EXCLUDED_KEYS.includes(key)) {
                    const value = selectedFilters[key as keyof IAuditionFilter].displayValue;
                    if (Array.isArray(value)) {
                        result.push(...value.filter(item => typeof item === 'string'));
                    } else {
                        result.push(value);
                    }
                }
            });
        return result;
    }, [selectedFilters]);

    useEffect(() => {
        if (selectedFilters) {
            setFilter(prevState => {
                const updatedFilters = ['total', ...extractFilters()];
                return [...new Set(updatedFilters)];
            });
        } else {
            setFilter(['total']);
        }
    }, [selectedFilters, extractFilters]);

    return (
        <Stack>
            {/* Search Bar + Filter Toggle */}
            <Stack direction="row" alignItems="center" gap={1.5} mb={2}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#1a1a1a',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        px: 3,
                        height: '64px',
                        '&:focus-within': {
                            borderColor: theme[themeStyle].primary_orange,
                        },
                        transition: 'border-color 0.2s ease',
                    }}>
                    <SearchIcon sx={{ color: '#9ea5ad', fontSize: 28, mr: 1.5 }} />
                    <InputBase
                        placeholder="Search auditions..."
                        sx={{
                            flex: 1,
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 18,
                            '& ::placeholder': { color: '#9ea5ad', opacity: 1, fontSize: 18 },
                        }}
                    />
                </Box>
                <IconButton
                    onClick={toggleFilterInMobile}
                    sx={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        width: '64px',
                        height: '64px',
                        '&:hover': {
                            backgroundColor: '#24292e',
                            borderColor: theme[themeStyle].primary_orange,
                        },
                        transition: 'all 0.2s ease',
                    }}>
                    <TuneIcon sx={{ color: theme[themeStyle].tag_white, fontSize: 22 }} />
                </IconButton>
            </Stack>

            {/* Filter Chips */}
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {filter.map((label, index) => (
                    <Chip
                        key={index}
                        label={label === 'total' ? `${label} (${totalResults})` : label}
                        onDelete={() => handleDelete(label)}
                        sx={{
                            textTransform: 'capitalize',
                            backgroundColor: colorsNice[index],
                            color: theme[themeStyle].true_black,
                        }}
                    />
                ))}
            </Box>

            {/* Audition Quick View Drawer */}
            <ReusableDrawer
                open={open}
                onClose={handleClose}
                anchor="right"
                content={
                    !!selectedAudition && (
                        <Container
                            disableGutters
                            maxWidth={false}
                            sx={{ height: '100vh', overflowY: 'auto', px: 0, py: 0 }}>
                            <AuditionWrapper audition={selectedAudition} disableSuggestion embeddedInDrawer />
                        </Container>
                    )
                }
            />

            {/* Card Grid */}
            <Grid container spacing={2}>
                {isEmpty(auditions) && <EmptySearchBlock />}
                {!isEmpty(auditions) &&
                    auditions.map((audition, index) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={audition._id || index}
                            sx={{
                                '@media (width: 1024px) and (height: 600px)': {
                                    flexBasis: '50%',
                                    maxWidth: '50%',
                                },
                                '@media (width: 1024px) and (height: 1366px)': {
                                    flexBasis: '50%',
                                    maxWidth: '50%',
                                },
                            }}>
                            <AuditionCard
                                audition={audition}
                                onViewAndApplyClick={() => handleOnViewAndApplyClick(audition)}
                            />
                        </Grid>
                    ))}
            </Grid>
        </Stack>
    );
};
